import React, { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from "recharts";
import {
  BarChart3, Download, Users, FolderKanban, Clock, DollarSign,
  TrendingUp, Calendar, AlertTriangle, PalmtreeIcon, FileSpreadsheet,
  ArrowUpRight, ArrowDownRight, Minus, Plus, Edit2, Trash2, Save
} from "lucide-react";
import { usePermissions } from "../contexts/PermissionsContext";

const COLORS = ["#F97316", "#3B82F6", "#10B981", "#8B5CF6", "#EF4444", "#F59E0B"];

// P&L Category definitions - Exact hierarchical structure
const PNL_CATEGORIES = {
  income: [
    { id: "sales", label: "Sales", type: "line_item" },
  ],
  cogs: [
    { id: "costOfGoodsSold", label: "Cost of goods sold", type: "sub_category" },
    { id: "costOfLaborCOGS", label: "Cost of labor - COGS", type: "line_item", parent: "costOfGoodsSold" },
    { id: "suppliesMaterialsCOGS", label: "Supplies & materials - COGS", type: "line_item", parent: "costOfGoodsSold" },
  ],
  expenses: [
    // Standalone line items (no sub-items)
    { id: "advertisingMarketing", label: "Advertising & marketing", type: "line_item" },
    { id: "businessLicenses", label: "Business licenses", type: "line_item" },
    { id: "contractLabor", label: "Contract labor", type: "line_item" },
    // Contributions to charities (sub-category)
    { id: "contributionsCharities", label: "Contributions to charities", type: "sub_category" },
    { id: "donation", label: "Donation", type: "line_item", parent: "contributionsCharities" },
    // Entertainment
    { id: "entertainment", label: "Entertainment", type: "line_item" },
    // General business expenses (sub-category)
    { id: "generalBusinessExpenses", label: "General business expenses", type: "sub_category" },
    { id: "bankFeesServiceCharges", label: "Bank fees & service charges", type: "line_item", parent: "generalBusinessExpenses" },
    { id: "continuingEducation", label: "Continuing education", type: "line_item", parent: "generalBusinessExpenses" },
    { id: "membershipsSubscriptions", label: "Memberships & subscriptions", type: "line_item", parent: "generalBusinessExpenses" },
    { id: "uniforms", label: "Uniforms", type: "line_item", parent: "generalBusinessExpenses" },
    // Insurance (sub-category)
    { id: "insuranceCategory", label: "Insurance", type: "sub_category" },
    { id: "insurance", label: "Insurance", type: "line_item", parent: "insuranceCategory" },
    { id: "businessInsurance", label: "Business insurance", type: "line_item", parent: "insuranceCategory" },
    // Legal & accounting services (sub-category)
    { id: "legalAccountingServices", label: "Legal & accounting services", type: "sub_category" },
    { id: "accountingFees", label: "Accounting fees", type: "line_item", parent: "legalAccountingServices" },
    { id: "legalFees", label: "Legal fees", type: "line_item", parent: "legalAccountingServices" },
    // Meals
    { id: "meals", label: "Meals", type: "line_item" },
    // Office expenses (sub-category)
    { id: "officeExpensesCategory", label: "Office expenses", type: "sub_category" },
    { id: "officeExpenses", label: "Office expenses", type: "line_item", parent: "officeExpensesCategory" },
    { id: "officeSupplies", label: "Office supplies", type: "line_item", parent: "officeExpensesCategory" },
    { id: "printingPhotocopying", label: "Printing & photocopying", type: "line_item", parent: "officeExpensesCategory" },
    { id: "shippingPostage", label: "Shipping & postage", type: "line_item", parent: "officeExpensesCategory" },
    { id: "smallToolsEquipment", label: "Small tools & equipment", type: "line_item", parent: "officeExpensesCategory" },
    { id: "softwareApps", label: "Software & apps", type: "line_item", parent: "officeExpensesCategory" },
    // Payroll expenses (sub-category)
    { id: "payrollExpensesCategory", label: "Payroll expenses", type: "sub_category" },
    { id: "payrollExpenses", label: "Payroll expenses", type: "line_item", parent: "payrollExpensesCategory" },
    { id: "payrollTaxes", label: "Taxes", type: "line_item", parent: "payrollExpensesCategory" },
    { id: "wages", label: "Wages", type: "line_item", parent: "payrollExpensesCategory" },
    // Rent (sub-category)
    { id: "rentCategory", label: "Rent", type: "sub_category" },
    { id: "equipmentRental", label: "Equipment rental", type: "line_item", parent: "rentCategory" },
    // Repairs & maintenance
    { id: "repairsMaintenance", label: "Repairs & maintenance", type: "line_item" },
    // Supplies (sub-category)
    { id: "suppliesCategory", label: "Supplies", type: "sub_category" },
    { id: "supplies", label: "Supplies", type: "line_item", parent: "suppliesCategory" },
    { id: "materials", label: "Materials", type: "line_item", parent: "suppliesCategory" },
    // Taxes paid (sub-category)
    { id: "taxesPaidCategory", label: "Taxes paid", type: "sub_category" },
    { id: "taxesPaid", label: "Taxes paid", type: "line_item", parent: "taxesPaidCategory" },
    { id: "payrollTaxesPaid", label: "Payroll taxes", type: "line_item", parent: "taxesPaidCategory" },
    // Travel (sub-category)
    { id: "travelCategory", label: "Travel", type: "sub_category" },
    { id: "travel", label: "Travel", type: "line_item", parent: "travelCategory" },
    { id: "airfare", label: "Airfare", type: "line_item", parent: "travelCategory" },
    { id: "hotels", label: "Hotels", type: "line_item", parent: "travelCategory" },
    { id: "vehicleRental", label: "Vehicle rental", type: "line_item", parent: "travelCategory" },
    // Utilities (sub-category)
    { id: "utilitiesCategory", label: "Utilities", type: "sub_category" },
    { id: "disposalWasteFees", label: "Disposal & waste fees", type: "line_item", parent: "utilitiesCategory" },
    { id: "electricity", label: "Electricity", type: "line_item", parent: "utilitiesCategory" },
    { id: "phoneService", label: "Phone service", type: "line_item", parent: "utilitiesCategory" },
    { id: "waterSewer", label: "Water & sewer", type: "line_item", parent: "utilitiesCategory" },
  ],
  otherIncome: [
    { id: "creditCardRewards", label: "Credit card rewards", type: "line_item" },
    { id: "rentalIncome", label: "Rental Income", type: "line_item" },
  ],
  otherExpenses: [
    // Vehicle expenses (sub-category)
    { id: "vehicleExpensesCategory", label: "Vehicle expenses", type: "sub_category" },
    { id: "vehicleGasFuel", label: "Vehicle gas & fuel", type: "line_item", parent: "vehicleExpensesCategory" },
    { id: "vehicleInsurance", label: "Vehicle insurance", type: "line_item", parent: "vehicleExpensesCategory" },
    { id: "vehicleRegistration", label: "Vehicle registration", type: "line_item", parent: "vehicleExpensesCategory" },
    { id: "vehicleRepairs", label: "Vehicle repairs", type: "line_item", parent: "vehicleExpensesCategory" },
    { id: "vehicleWashRoadServices", label: "Vehicle wash & road services", type: "line_item", parent: "vehicleExpensesCategory" },
  ]
};

const SECTION_LABELS = {
  income: "Income",
  cogs: "Cost of Goods Sold",
  expenses: "Expenses",
  otherIncome: "Other Income",
  otherExpenses: "Other Expenses"
};

// Helper to format currency safely (handles null/undefined)
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return `$${Number(value).toLocaleString()}`;
};

// Helper to render hierarchical dropdown options
const renderCategoryOptions = (categories) => {
  return categories.map(cat => {
    const isSubCategory = cat.type === 'sub_category';
    const isNested = cat.parent;
    const indent = isNested ? '    → ' : isSubCategory ? '' : '';
    const style = isSubCategory ? 'font-semibold' : '';
    return {
      ...cat,
      displayLabel: indent + cat.label,
      className: style
    };
  });
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Helper to get category label from ID
const getCategoryLabel = (section, categoryId) => {
  const categories = PNL_CATEGORIES[section] || [];
  const cat = categories.find(c => c.id === categoryId);
  return cat ? cat.label : categoryId;
};

const ReportsPage = () => {
  const { can } = usePermissions();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(1)).toISOString().split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Report data
  const [projectReport, setProjectReport] = useState([]);
  const [financialReport, setFinancialReport] = useState(null);
  const [employeeHours, setEmployeeHours] = useState([]);
  const [projectHours, setProjectHours] = useState([]);
  const [overtimeReport, setOvertimeReport] = useState([]);
  const [timeOffBalances, setTimeOffBalances] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [pnlData, setPnlData] = useState(null);
  const [pnlLoading, setPnlLoading] = useState(false);
  
  // P&L Entry Management State
  const [showPnlEntryModal, setShowPnlEntryModal] = useState(false);
  const [pnlEntries, setPnlEntries] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showEntriesPanel, setShowEntriesPanel] = useState(false);
  const [pnlEntryForm, setPnlEntryForm] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    section: 'income',
    category: 'sales',
    custom_label: '',
    amount: '',
    description: ''
  });

  const canManage = ["owner", "office_manager", "project_manager"].includes(user?.role);
  const canViewFinancials = can('view_company_financials') || can('view_profit_margins') || canManage;
  const canEditFinancials = can('manage_company_financials') || ["owner", "office_manager"].includes(user?.role);

  // Fetch P&L entries
  const fetchPnlEntries = useCallback(async () => {
    try {
      const response = await API.get(`/pnl-entries?year=${selectedYear}`);
      setPnlEntries(response.data || []);
    } catch (error) {
      console.error("Error fetching P&L entries:", error);
    }
  }, [selectedYear]);

  // Fetch P&L data
  const fetchPnLData = useCallback(async () => {
    setPnlLoading(true);
    try {
      const response = await API.get(`/reports/profit-loss?year=${selectedYear}`);
      // Check if we have real data - could be from invoices/payments OR manual P&L entries
      // dataSource === 'actual' indicates we successfully fetched from backend
      if (response.data && response.data.dataSource === 'actual') {
        setPnlData(response.data);
      } else if (response.data && response.data.ytd) {
        // Check if any YTD values are non-zero (could be manual entries with no revenue)
        const hasAnyData = Object.values(response.data.ytd).some(v => typeof v === 'number' && v !== 0);
        if (hasAnyData) {
          setPnlData(response.data);
        } else {
          // No real data at all, use mock for demo purposes
          generateMockPnLData();
        }
      } else {
        generateMockPnLData();
      }
    } catch (error) {
      console.error("Error fetching P&L:", error);
      // Generate mock P&L data if endpoint doesn't exist or fails
      generateMockPnLData();
    } finally {
      setPnlLoading(false);
    }
  }, [selectedYear]);

  // Generate comprehensive mock P&L data
  const generateMockPnLData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();
    
    const monthlyData = months.map((month, idx) => {
      const isCurrentOrPast = idx <= currentMonth;
      const seasonalMultiplier = idx >= 3 && idx <= 9 ? 1.3 : 0.8; // Construction season
      const baseRevenue = isCurrentOrPast ? Math.floor(150000 + Math.random() * 100000) * seasonalMultiplier : 0;
      
      // Revenue breakdown
      const contractRevenue = Math.floor(baseRevenue * 0.85);
      const changeOrders = Math.floor(baseRevenue * 0.12);
      const otherIncome = Math.floor(baseRevenue * 0.03);
      const totalRevenue = contractRevenue + changeOrders + otherIncome;
      
      // Cost of Goods Sold (Direct Costs)
      const materialsCost = Math.floor(totalRevenue * 0.32);
      const laborDirect = Math.floor(totalRevenue * 0.28);
      const subcontractorCosts = Math.floor(totalRevenue * 0.15);
      const equipmentRental = Math.floor(totalRevenue * 0.03);
      const permitsFees = Math.floor(totalRevenue * 0.02);
      const totalCOGS = materialsCost + laborDirect + subcontractorCosts + equipmentRental + permitsFees;
      
      // Gross Profit
      const grossProfit = totalRevenue - totalCOGS;
      const grossMargin = totalRevenue > 0 ? ((grossProfit / totalRevenue) * 100).toFixed(1) : 0;
      
      // Operating Expenses
      const salariesAdmin = Math.floor(25000 + Math.random() * 5000);
      const officeRent = 4500;
      const utilities = Math.floor(800 + Math.random() * 200);
      const insurance = 3200;
      const vehicleExpenses = Math.floor(2500 + Math.random() * 1000);
      const marketing = Math.floor(1500 + Math.random() * 1000);
      const software = 1200;
      const professional = Math.floor(1000 + Math.random() * 500);
      const depreciation = 2000;
      const miscExpenses = Math.floor(500 + Math.random() * 500);
      const totalOpex = salariesAdmin + officeRent + utilities + insurance + vehicleExpenses + 
                        marketing + software + professional + depreciation + miscExpenses;
      
      // Operating Income
      const operatingIncome = grossProfit - totalOpex;
      
      // Other Income/Expenses
      const interestIncome = Math.floor(100 + Math.random() * 100);
      const interestExpense = Math.floor(500 + Math.random() * 200);
      const otherExpenses = Math.floor(Math.random() * 300);
      const totalOther = interestIncome - interestExpense - otherExpenses;
      
      // Net Income
      const netIncome = operatingIncome + totalOther;
      const netMargin = totalRevenue > 0 ? ((netIncome / totalRevenue) * 100).toFixed(1) : 0;
      
      return {
        month,
        monthNum: idx + 1,
        // Revenue
        contractRevenue: isCurrentOrPast ? contractRevenue : null,
        changeOrders: isCurrentOrPast ? changeOrders : null,
        otherIncome: isCurrentOrPast ? otherIncome : null,
        totalRevenue: isCurrentOrPast ? totalRevenue : null,
        // COGS
        materialsCost: isCurrentOrPast ? materialsCost : null,
        laborDirect: isCurrentOrPast ? laborDirect : null,
        subcontractorCosts: isCurrentOrPast ? subcontractorCosts : null,
        equipmentRental: isCurrentOrPast ? equipmentRental : null,
        permitsFees: isCurrentOrPast ? permitsFees : null,
        totalCOGS: isCurrentOrPast ? totalCOGS : null,
        // Gross Profit
        grossProfit: isCurrentOrPast ? grossProfit : null,
        grossMargin: isCurrentOrPast ? grossMargin : null,
        // Operating Expenses
        salariesAdmin: isCurrentOrPast ? salariesAdmin : null,
        officeRent: isCurrentOrPast ? officeRent : null,
        utilities: isCurrentOrPast ? utilities : null,
        insurance: isCurrentOrPast ? insurance : null,
        vehicleExpenses: isCurrentOrPast ? vehicleExpenses : null,
        marketing: isCurrentOrPast ? marketing : null,
        software: isCurrentOrPast ? software : null,
        professional: isCurrentOrPast ? professional : null,
        depreciation: isCurrentOrPast ? depreciation : null,
        miscExpenses: isCurrentOrPast ? miscExpenses : null,
        totalOpex: isCurrentOrPast ? totalOpex : null,
        // Operating Income
        operatingIncome: isCurrentOrPast ? operatingIncome : null,
        // Other
        interestIncome: isCurrentOrPast ? interestIncome : null,
        interestExpense: isCurrentOrPast ? interestExpense : null,
        otherExpenses: isCurrentOrPast ? otherExpenses : null,
        totalOther: isCurrentOrPast ? totalOther : null,
        // Net Income
        netIncome: isCurrentOrPast ? netIncome : null,
        netMargin: isCurrentOrPast ? netMargin : null,
      };
    });
    
    // Calculate YTD totals
    const ytdData = monthlyData.reduce((acc, m) => {
      if (m.totalRevenue !== null) {
        Object.keys(m).forEach(key => {
          if (key !== 'month' && key !== 'monthNum' && key !== 'grossMargin' && key !== 'netMargin') {
            acc[key] = (acc[key] || 0) + (m[key] || 0);
          }
        });
      }
      return acc;
    }, {});
    
    ytdData.grossMargin = ytdData.totalRevenue > 0 ? ((ytdData.grossProfit / ytdData.totalRevenue) * 100).toFixed(1) : 0;
    ytdData.netMargin = ytdData.totalRevenue > 0 ? ((ytdData.netIncome / ytdData.totalRevenue) * 100).toFixed(1) : 0;
    
    setPnlData({
      year: selectedYear,
      monthly: monthlyData,
      ytd: ytdData,
      previousYearComparison: {
        revenueChange: 12.5,
        grossProfitChange: 8.3,
        netIncomeChange: 15.2
      }
    });
  };

  useEffect(() => {
    if (activeTab === 'pnl' && canViewFinancials) {
      fetchPnLData();
      fetchPnlEntries();
    }
  }, [activeTab, fetchPnLData, fetchPnlEntries, canViewFinancials]);

  // Helper to open modal with pre-filled section/category
  const openEntryForCategory = (section, category, month = null) => {
    if (!canEditFinancials) return;
    setEditingEntry(null);
    setPnlEntryForm({
      year: selectedYear,
      month: month || new Date().getMonth() + 1,
      section: section,
      category: category,
      custom_label: '',
      amount: '',
      description: ''
    });
    setShowPnlEntryModal(true);
  };

  // P&L Entry CRUD functions
  const handleOpenPnlEntryModal = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setPnlEntryForm({
        year: entry.year,
        month: entry.month,
        section: entry.section,
        category: entry.category,
        custom_label: entry.custom_label || '',
        amount: entry.amount.toString(),
        description: entry.description || ''
      });
    } else {
      setEditingEntry(null);
      setPnlEntryForm({
        year: selectedYear,
        month: new Date().getMonth() + 1,
        section: 'income',
        category: 'sales',
        custom_label: '',
        amount: '',
        description: ''
      });
    }
    setShowPnlEntryModal(true);
  };

  const handleSavePnlEntry = async () => {
    if (!pnlEntryForm.amount || isNaN(parseFloat(pnlEntryForm.amount))) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      const payload = {
        ...pnlEntryForm,
        amount: parseFloat(pnlEntryForm.amount),
        year: parseInt(pnlEntryForm.year),
        month: parseInt(pnlEntryForm.month)
      };

      if (editingEntry) {
        await API.put(`/pnl-entries/${editingEntry.entry_id}`, payload);
        toast.success("P&L entry updated");
      } else {
        await API.post("/pnl-entries", payload);
        toast.success("P&L entry added");
      }

      setShowPnlEntryModal(false);
      fetchPnLData();
      fetchPnlEntries();
    } catch (error) {
      console.error("Error saving P&L entry:", error);
      toast.error("Failed to save P&L entry");
    }
  };

  const handleDeletePnlEntry = async (entryId) => {
    if (!confirm("Are you sure you want to delete this entry?")) return;
    
    try {
      await API.delete(`/pnl-entries/${entryId}`);
      toast.success("P&L entry deleted");
      fetchPnLData();
      fetchPnlEntries();
    } catch (error) {
      console.error("Error deleting P&L entry:", error);
      toast.error("Failed to delete P&L entry");
    }
  };

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        API.get("/reports/project-summary"),
        API.get("/reports/financial"),
      ];
      
      if (canManage) {
        promises.push(
          API.get("/time-reports/by-employee", { params: { start_date: dateRange.start, end_date: dateRange.end } }),
          API.get("/time-reports/by-project", { params: { start_date: dateRange.start, end_date: dateRange.end } }),
          API.get("/time-reports/overtime", { params: { start_date: dateRange.start, end_date: dateRange.end } }),
          API.get("/time-reports/time-off-balances"),
          API.get("/timesheets", { params: { status: "approved" } })
        );
      }

      const results = await Promise.all(promises);
      setProjectReport(results[0].data);
      setFinancialReport(results[1].data);
      
      if (canManage && results.length > 2) {
        setEmployeeHours(results[2].data);
        setProjectHours(results[3].data);
        setOvertimeReport(results[4].data);
        setTimeOffBalances(results[5].data);
        setTimesheets(results[6].data);
      }
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to load some reports");
    } finally {
      setLoading(false);
    }
  }, [dateRange, canManage]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Export functions
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error("No data to export");
      return;
    }
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(","),
      ...data.map((row) => headers.map((h) => `"${row[h] ?? ""}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filename}.csv`);
  };

  const exportTimesheetsToCSV = () => {
    const exportData = timesheets.map(ts => ({
      employee: ts.user_name,
      week_start: ts.week_start,
      week_end: ts.week_end,
      regular_hours: ts.regular_hours,
      overtime_hours: ts.overtime_hours,
      total_hours: ts.total_hours,
      status: ts.status,
      approved_at: ts.approved_at || ""
    }));
    exportToCSV(exportData, "approved_timesheets");
  };

  // Calculate totals
  const totalBudget = projectReport.reduce((sum, p) => sum + (p.budget || 0), 0);
  const totalCost = projectReport.reduce((sum, p) => sum + (p.total_cost || 0), 0);
  const totalProfit = projectReport.reduce((sum, p) => sum + (p.gross_profit || 0), 0);
  const totalHours = employeeHours.reduce((sum, e) => sum + (e.total_hours || 0), 0);
  const totalOvertime = employeeHours.reduce((sum, e) => sum + (e.overtime_hours || 0), 0);

  if (loading) {
    return <div className="animate-pulse">Loading reports...</div>;
  }

  return (
    <div data-testid="reports-page" className="space-y-6 animate-slide-in">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-chivo font-bold text-white tracking-tight">Reports</h1>
          <p className="text-slate-400 font-ibm mt-1">Analytics, time reports, and financial overview</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-slate-400" />
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-36"
            />
            <span className="text-slate-400">to</span>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-36"
            />
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 size={16} />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="pnl" className="flex items-center gap-2" data-testid="pnl-tab">
            <FileSpreadsheet size={16} />
            <span className="hidden sm:inline">P&L</span>
          </TabsTrigger>
          <TabsTrigger value="time" className="flex items-center gap-2">
            <Clock size={16} />
            <span className="hidden sm:inline">Time</span>
          </TabsTrigger>
          <TabsTrigger value="projects" className="flex items-center gap-2">
            <FolderKanban size={16} />
            <span className="hidden sm:inline">Projects</span>
          </TabsTrigger>
          <TabsTrigger value="payroll" className="flex items-center gap-2">
            <DollarSign size={16} />
            <span className="hidden sm:inline">Payroll</span>
          </TabsTrigger>
          <TabsTrigger value="timeoff" className="flex items-center gap-2">
            <PalmtreeIcon size={16} />
            <span className="hidden sm:inline">Time Off</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <div className="flex items-center gap-2 text-slate-400 mb-2">
                <DollarSign size={18} />
                <span className="text-sm font-medium">Total Budget</span>
              </div>
              <p className="text-3xl font-chivo font-bold text-white">
                ${totalBudget.toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <div className="flex items-center gap-2 text-amber-600 mb-2">
                <TrendingUp size={18} />
                <span className="text-sm font-medium">Total Cost</span>
              </div>
              <p className="text-3xl font-chivo font-bold text-amber-600">
                ${totalCost.toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <div className="flex items-center gap-2 text-emerald-600 mb-2">
                <DollarSign size={18} />
                <span className="text-sm font-medium">Gross Profit</span>
              </div>
              <p className={`text-3xl font-chivo font-bold ${totalProfit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                ${totalProfit.toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <div className="flex items-center gap-2 text-blue-600 mb-2">
                <Clock size={18} />
                <span className="text-sm font-medium">Total Hours</span>
              </div>
              <p className="text-3xl font-chivo font-bold text-blue-600">
                {totalHours.toFixed(1)}h
              </p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Project Profit Chart */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="font-chivo font-semibold text-lg text-white mb-4">Project Profitability</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={projectReport.slice(0, 6)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="budget" fill="#3B82F6" name="Budget" />
                  <Bar dataKey="total_cost" fill="#F97316" name="Cost" />
                  <Bar dataKey="gross_profit" fill="#10B981" name="Profit" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Hours by Project Pie */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="font-chivo font-semibold text-lg text-white mb-4">Hours by Project</h3>
              {projectHours.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={projectHours}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name.slice(0, 10)}... (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="total_hours"
                      nameKey="project_name"
                    >
                      {projectHours.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value.toFixed(1)}h`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  No time data available
                </div>
              )}
            </div>
          </div>

          {/* Financial Overview */}
          {financialReport && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="font-chivo font-semibold text-lg text-white mb-4">Payment Collection</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-slate-400">Received</p>
                  <p className="text-2xl font-bold text-emerald-600">${financialReport.total_received?.toLocaleString()}</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-sm text-slate-400">Pending</p>
                  <p className="text-2xl font-bold text-amber-600">${financialReport.total_pending?.toLocaleString()}</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-slate-400">Overdue</p>
                  <p className="text-2xl font-bold text-red-600">${financialReport.total_overdue?.toLocaleString()}</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-slate-400">Collection Rate</p>
                  <p className="text-2xl font-bold text-blue-600">{financialReport.collection_rate?.toFixed(1)}%</p>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Profit & Loss Tab */}
        <TabsContent value="pnl" className="mt-6 space-y-6" data-testid="pnl-content">
          {!canViewFinancials ? (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
              <FileSpreadsheet size={48} className="mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Access Restricted</h3>
              <p className="text-slate-500">You do not have permission to view financial reports</p>
            </div>
          ) : pnlLoading ? (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-4"></div>
              <p className="text-slate-400">Loading Profit & Loss Statement...</p>
            </div>
          ) : pnlData ? (
            <>
              {/* P&L Header with Year Selector */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-chivo font-bold text-white">Profit & Loss Statement</h2>
                  <p className="text-slate-400">Comprehensive income statement for {selectedYear}</p>
                </div>
                <div className="flex items-center gap-4">
                  {canEditFinancials && (
                    <Button 
                      onClick={() => handleOpenPnlEntryModal()} 
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-900"
                      data-testid="add-pnl-entry-btn"
                    >
                      <Plus size={16} className="mr-2" />
                      Add Entry
                    </Button>
                  )}
                  <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-32 bg-slate-800 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {[2024, 2025, 2026].map(year => (
                        <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={() => {
                    const csvData = pnlData.monthly.map(m => ({
                      Month: m.month,
                      'Contract Revenue': m.contractRevenue,
                      'Change Orders': m.changeOrders,
                      'Other Income': m.otherIncome,
                      'Total Revenue': m.totalRevenue,
                      'Materials': m.materialsCost,
                      'Direct Labor': m.laborDirect,
                      'Subcontractors': m.subcontractorCosts,
                      'Equipment': m.equipmentRental,
                      'Permits/Fees': m.permitsFees,
                      'Total COGS': m.totalCOGS,
                      'Gross Profit': m.grossProfit,
                      'Gross Margin %': m.grossMargin,
                      'Admin Salaries': m.salariesAdmin,
                      'Rent': m.officeRent,
                      'Utilities': m.utilities,
                      'Insurance': m.insurance,
                      'Vehicles': m.vehicleExpenses,
                      'Marketing': m.marketing,
                      'Software': m.software,
                      'Professional': m.professional,
                      'Depreciation': m.depreciation,
                      'Misc': m.miscExpenses,
                      'Total Opex': m.totalOpex,
                      'Operating Income': m.operatingIncome,
                      'Interest Income': m.interestIncome,
                      'Interest Expense': m.interestExpense,
                      'Other Expenses': m.otherExpenses,
                      'Net Income': m.netIncome,
                      'Net Margin %': m.netMargin
                    }));
                    exportToCSV(csvData, `PnL_${selectedYear}`);
                  }} className="border-slate-700">
                    <Download size={16} className="mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>

              {/* YTD Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-emerald-900/50 to-slate-900 border border-emerald-700/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-emerald-400 font-medium">YTD Revenue</span>
                    {pnlData.previousYearComparison && (
                      <span className={`flex items-center text-xs ${pnlData.previousYearComparison.revenueChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {pnlData.previousYearComparison.revenueChange >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                        {Math.abs(pnlData.previousYearComparison.revenueChange)}%
                      </span>
                    )}
                  </div>
                  <p className="text-3xl font-mono font-bold text-white">{formatCurrency(pnlData.ytd?.totalRevenue)}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-900/50 to-slate-900 border border-blue-700/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-blue-400 font-medium">Gross Profit</span>
                    <span className="text-xs text-blue-400">{pnlData.ytd?.grossMargin || 0}% margin</span>
                  </div>
                  <p className="text-3xl font-mono font-bold text-white">{formatCurrency(pnlData.ytd?.grossProfit)}</p>
                </div>
                <div className="bg-gradient-to-br from-amber-900/50 to-slate-900 border border-amber-700/30 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-amber-400 font-medium">Operating Expenses</span>
                  </div>
                  <p className="text-3xl font-mono font-bold text-white">{formatCurrency(pnlData.ytd?.totalOpex)}</p>
                </div>
                <div className={`bg-gradient-to-br ${(pnlData.ytd?.netIncome || 0) >= 0 ? 'from-emerald-900/50 border-emerald-700/30' : 'from-red-900/50 border-red-700/30'} to-slate-900 border rounded-xl p-6`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-medium ${(pnlData.ytd?.netIncome || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>Net Income</span>
                    <span className={`text-xs ${(pnlData.ytd?.netIncome || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{pnlData.ytd?.netMargin || 0}% margin</span>
                  </div>
                  <p className={`text-3xl font-mono font-bold ${(pnlData.ytd?.netIncome || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrency(Math.abs(pnlData.ytd?.netIncome || 0))}
                  </p>
                </div>
              </div>

              {/* Net Income Trend Chart */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <h3 className="font-chivo font-semibold text-lg text-white mb-4">Monthly Revenue vs Net Income</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={pnlData.monthly.filter(m => m.totalRevenue !== null)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month" stroke="#94a3b8" />
                    <YAxis stroke="#94a3b8" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                      formatter={(value) => `$${value?.toLocaleString()}`}
                    />
                    <Legend />
                    <Bar dataKey="totalRevenue" fill="#10b981" name="Revenue" />
                    <Bar dataKey="netIncome" fill="#3b82f6" name="Net Income" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Manual P&L Entries Management Panel */}
              {canEditFinancials && pnlEntries.length > 0 && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                  <button 
                    onClick={() => setShowEntriesPanel(!showEntriesPanel)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Edit2 size={18} className="text-amber-400" />
                      <div className="text-left">
                        <h3 className="font-chivo font-semibold text-white">Manual Entries ({pnlEntries.length})</h3>
                        <p className="text-sm text-slate-500">Click to {showEntriesPanel ? 'hide' : 'view and edit'} your manual P&L entries</p>
                      </div>
                    </div>
                    <div className={`transform transition-transform ${showEntriesPanel ? 'rotate-180' : ''}`}>
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {showEntriesPanel && (
                    <div className="border-t border-slate-800">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-800/50">
                              <th className="px-4 py-3 text-left text-slate-400 font-medium">Month</th>
                              <th className="px-4 py-3 text-left text-slate-400 font-medium">Section</th>
                              <th className="px-4 py-3 text-left text-slate-400 font-medium">Category</th>
                              <th className="px-4 py-3 text-right text-slate-400 font-medium">Amount</th>
                              <th className="px-4 py-3 text-left text-slate-400 font-medium">Description</th>
                              <th className="px-4 py-3 text-right text-slate-400 font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800">
                            {pnlEntries.map(entry => (
                              <tr key={entry.entry_id} className="hover:bg-slate-800/30">
                                <td className="px-4 py-3 text-white font-medium">
                                  {MONTHS[entry.month - 1]} {entry.year}
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                                    entry.section === 'income' ? 'bg-emerald-900/50 text-emerald-400' :
                                    entry.section === 'cogs' ? 'bg-amber-900/50 text-amber-400' :
                                    entry.section === 'expenses' ? 'bg-red-900/50 text-red-400' :
                                    entry.section === 'otherIncome' ? 'bg-teal-900/50 text-teal-400' :
                                    'bg-orange-900/50 text-orange-400'
                                  }`}>
                                    {SECTION_LABELS[entry.section] || entry.section}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-slate-300">
                                  {getCategoryLabel(entry.section, entry.category)}
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-white">
                                  ${entry.amount.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-slate-400 max-w-[200px] truncate">
                                  {entry.description || '-'}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      onClick={() => handleOpenPnlEntryModal(entry)}
                                      className="p-1.5 rounded hover:bg-slate-700 text-blue-400 hover:text-blue-300 transition-colors"
                                      title="Edit entry"
                                    >
                                      <Edit2 size={14} />
                                    </button>
                                    <button
                                      onClick={() => handleDeletePnlEntry(entry.entry_id)}
                                      className="p-1.5 rounded hover:bg-slate-700 text-red-400 hover:text-red-300 transition-colors"
                                      title="Delete entry"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Detailed P&L Table */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
                  <div>
                    <h3 className="font-chivo font-semibold text-lg text-white">Detailed Profit & Loss Statement</h3>
                    <p className="text-sm text-slate-500">Month-by-month breakdown with YTD totals • Click any value to add/edit entry</p>
                  </div>
                  {canEditFinancials && (
                    <Button 
                      onClick={() => handleOpenPnlEntryModal()} 
                      size="sm"
                      className="bg-emerald-500 hover:bg-emerald-400 text-slate-900"
                    >
                      <Plus size={14} className="mr-1" />
                      Add Entry
                    </Button>
                  )}
                </div>
                <div className="overflow-x-auto max-w-full" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  <table className="w-full text-sm min-w-[1400px]" data-testid="pnl-table">
                    <thead className="sticky top-0 z-20">
                      <tr className="bg-slate-800">
                        <th className="px-4 py-3 text-left text-slate-300 font-semibold sticky left-0 bg-slate-800 z-30 min-w-[220px]">Category</th>
                        {pnlData.monthly.map(m => (
                          <th key={m.month} className="px-3 py-3 text-right text-slate-300 font-medium min-w-[85px]">{m.month}</th>
                        ))}
                        <th className="px-4 py-3 text-right text-emerald-400 font-bold min-w-[100px] bg-emerald-900/30">YTD Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {/* INCOME SECTION */}
                      <tr className="bg-emerald-900/20">
                        <td colSpan={14} className="px-4 py-2 font-bold text-emerald-400 uppercase tracking-wider text-xs">Income</td>
                      </tr>
                      <tr className={`hover:bg-slate-800/50 ${canEditFinancials ? 'cursor-pointer' : ''}`} onClick={() => openEntryForCategory('income', 'sales')}>
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-900 flex items-center gap-2">
                          Sales
                          {canEditFinancials && <Edit2 size={12} className="text-slate-600" />}
                        </td>
                        {pnlData.monthly.map((m, idx) => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400 hover:bg-emerald-900/20" onClick={(e) => { e.stopPropagation(); openEntryForCategory('income', 'sales', idx + 1); }}>{formatCurrency(m.sales || m.totalRevenue)}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.sales || pnlData.ytd?.totalRevenue)}</td>
                      </tr>
                      <tr className="bg-emerald-900/30 font-semibold">
                        <td className="px-4 py-2 text-emerald-400 sticky left-0 bg-emerald-900/30">Total for Income</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-emerald-400">{formatCurrency(m.totalRevenue)}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-bold text-emerald-400 bg-emerald-900/40">{formatCurrency(pnlData.ytd?.totalRevenue)}</td>
                      </tr>

                      {/* COST OF GOODS SOLD SECTION */}
                      <tr className="bg-amber-900/20">
                        <td colSpan={14} className="px-4 py-2 font-bold text-amber-400 uppercase tracking-wider text-xs">Cost of Goods Sold</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-amber-300 font-medium sticky left-0 bg-slate-900">Cost of goods sold</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Cost of labor - COGS</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.laborDirect ? `$${m.laborDirect.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.laborDirect)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Supplies & materials - COGS</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.materialsCost ? `$${m.materialsCost.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.materialsCost)}</td>
                      </tr>
                      <tr className="bg-amber-900/30 font-semibold">
                        <td className="px-4 py-2 text-amber-400 sticky left-0 bg-amber-900/30">Total for Cost of Goods Sold</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-amber-400">{m.totalCOGS ? `$${m.totalCOGS.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-bold text-amber-400 bg-amber-900/40">{formatCurrency(pnlData.ytd?.totalCOGS)}</td>
                      </tr>

                      {/* GROSS PROFIT */}
                      <tr className="bg-blue-900/30 font-bold">
                        <td className="px-4 py-3 text-blue-400 sticky left-0 bg-blue-900/30">GROSS PROFIT</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-3 text-right font-mono text-blue-400">{m.grossProfit ? `$${m.grossProfit.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-3 text-right font-mono font-bold text-blue-400 bg-blue-900/40">{formatCurrency(pnlData.ytd?.grossProfit)}</td>
                      </tr>

                      {/* EXPENSES SECTION */}
                      <tr className="bg-red-900/20">
                        <td colSpan={14} className="px-4 py-2 font-bold text-red-400 uppercase tracking-wider text-xs">Expenses</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-900">Advertising & marketing</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.marketing ? `$${m.marketing.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.marketing)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-900">Business licenses</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.businessLicenses ? `$${m.businessLicenses.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.businessLicenses)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-900">Contract labor</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.contractLabor ? `$${m.contractLabor.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.contractLabor)}</td>
                      </tr>
                      {/* Contributions to charities */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Contributions to charities</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Donation</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.donations ? `$${m.donations.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.donations)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-900">Entertainment</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.entertainment ? `$${m.entertainment.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.entertainment)}</td>
                      </tr>
                      {/* General business expenses */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">General business expenses</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Bank fees & service charges</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.bankFees ? `$${m.bankFees.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.bankFees)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Continuing education</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.continuingEducation ? `$${m.continuingEducation.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.continuingEducation)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Memberships & subscriptions</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.memberships ? `$${m.memberships.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.memberships)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Uniforms</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.uniforms ? `$${m.uniforms.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.uniforms)}</td>
                      </tr>
                      {/* Insurance */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Insurance</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Insurance</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.insurance ? `$${m.insurance.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.insurance)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Business insurance</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.businessInsurance ? `$${m.businessInsurance.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.businessInsurance)}</td>
                      </tr>
                      {/* Legal & accounting */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Legal & accounting services</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Accounting fees</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.accountingFees ? `$${m.accountingFees.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.accountingFees)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Legal fees</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.legalFees ? `$${m.legalFees.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.legalFees)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-900">Meals</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.meals ? `$${m.meals.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.meals)}</td>
                      </tr>
                      {/* Office expenses */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Office expenses</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Office supplies</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.officeSupplies ? `$${m.officeSupplies.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.officeSupplies)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Printing & photocopying</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.printing ? `$${m.printing.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.printing)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Shipping & postage</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.shipping ? `$${m.shipping.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.shipping)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Small tools & equipment</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.smallTools ? `$${m.smallTools.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.smallTools)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Software & apps</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.software ? `$${m.software.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.software)}</td>
                      </tr>
                      {/* Payroll expenses */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Payroll expenses</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Taxes</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.payrollTaxes ? `$${m.payrollTaxes.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.payrollTaxes)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Wages</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.salariesAdmin ? `$${m.salariesAdmin.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.salariesAdmin)}</td>
                      </tr>
                      {/* Rent */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Rent</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.officeRent ? `$${m.officeRent.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.officeRent)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Equipment rental</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.equipmentRental ? `$${m.equipmentRental.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.equipmentRental)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-900">Repairs & maintenance</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.repairs ? `$${m.repairs.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.repairs)}</td>
                      </tr>
                      {/* Supplies */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Supplies</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Materials</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.suppliesMaterials ? `$${m.suppliesMaterials.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.suppliesMaterials)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Supplies</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.suppliesGeneral ? `$${m.suppliesGeneral.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.suppliesGeneral)}</td>
                      </tr>
                      {/* Taxes paid */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Taxes paid</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Payroll taxes</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.payrollTaxesPaid ? `$${m.payrollTaxesPaid.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.payrollTaxesPaid)}</td>
                      </tr>
                      {/* Travel */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Travel</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Airfare</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.airfare ? `$${m.airfare.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.airfare)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Hotels</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.hotels ? `$${m.hotels.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.hotels)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Vehicle rental</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.vehicleRental ? `$${m.vehicleRental.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.vehicleRental)}</td>
                      </tr>
                      {/* Utilities */}
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-red-300 font-medium sticky left-0 bg-slate-900">Utilities</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.utilities ? `$${m.utilities.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.utilities)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Disposal & waste fees</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.disposal ? `$${m.disposal.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.disposal)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Electricity</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.electricity ? `$${m.electricity.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.electricity)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Phone service</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.phone ? `$${m.phone.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.phone)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Water & sewer</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-400">{m.water ? `$${m.water.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-white bg-slate-800/30">{formatCurrency(pnlData.ytd?.water)}</td>
                      </tr>
                      <tr className="bg-red-900/30 font-semibold">
                        <td className="px-4 py-2 text-red-400 sticky left-0 bg-red-900/30">Total for Expenses</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-red-400">{m.totalOpex ? `$${m.totalOpex.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-bold text-red-400 bg-red-900/40">{formatCurrency(pnlData.ytd?.totalOpex)}</td>
                      </tr>

                      {/* NET OPERATING INCOME */}
                      <tr className="bg-purple-900/30 font-bold">
                        <td className="px-4 py-3 text-purple-400 sticky left-0 bg-purple-900/30">Net Operating Income</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className={`px-3 py-3 text-right font-mono ${m.operatingIncome >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                            {m.operatingIncome !== null ? `$${m.operatingIncome.toLocaleString()}` : '-'}
                          </td>
                        ))}
                        <td className={`px-4 py-3 text-right font-mono font-bold bg-purple-900/40 ${pnlData.ytd.operatingIncome >= 0 ? 'text-purple-400' : 'text-red-400'}`}>
                          {formatCurrency(pnlData.ytd?.operatingIncome)}
                        </td>
                      </tr>

                      {/* OTHER INCOME */}
                      <tr className="bg-teal-900/20">
                        <td colSpan={14} className="px-4 py-2 font-bold text-teal-400 uppercase tracking-wider text-xs">Other Income</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-900">Credit card rewards</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-emerald-400">{m.creditCardRewards ? `$${m.creditCardRewards.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-emerald-400 bg-slate-800/30">{formatCurrency(pnlData.ytd?.creditCardRewards)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-900">Rental Income</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-emerald-400">{m.rentalIncome ? `$${m.rentalIncome.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-emerald-400 bg-slate-800/30">{formatCurrency(pnlData.ytd?.rentalIncome)}</td>
                      </tr>
                      <tr className="bg-teal-900/30 font-semibold">
                        <td className="px-4 py-2 text-teal-400 sticky left-0 bg-teal-900/30">Total for Other Income</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-teal-400">{m.totalOtherIncome ? `$${m.totalOtherIncome.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-bold text-teal-400 bg-teal-900/40">{formatCurrency(pnlData.ytd?.totalOtherIncome)}</td>
                      </tr>

                      {/* OTHER EXPENSES */}
                      <tr className="bg-orange-900/20">
                        <td colSpan={14} className="px-4 py-2 font-bold text-orange-400 uppercase tracking-wider text-xs">Other Expenses</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-orange-300 font-medium sticky left-0 bg-slate-900">Vehicle expenses</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-slate-500">-</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono text-slate-500 bg-slate-800/30">-</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Vehicle gas & fuel</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-red-400">{m.vehicleGas ? `$${m.vehicleGas.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-red-400 bg-slate-800/30">{formatCurrency(pnlData.ytd?.vehicleGas)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Vehicle insurance</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-red-400">{m.vehicleInsurance ? `$${m.vehicleInsurance.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-red-400 bg-slate-800/30">{formatCurrency(pnlData.ytd?.vehicleInsurance)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Vehicle registration</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-red-400">{m.vehicleRegistration ? `$${m.vehicleRegistration.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-red-400 bg-slate-800/30">{formatCurrency(pnlData.ytd?.vehicleRegistration)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Vehicle repairs</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-red-400">{m.vehicleRepairs ? `$${m.vehicleRepairs.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-red-400 bg-slate-800/30">{formatCurrency(pnlData.ytd?.vehicleRepairs)}</td>
                      </tr>
                      <tr className="hover:bg-slate-800/50">
                        <td className="px-4 py-2 text-slate-300 pl-8 sticky left-0 bg-slate-900">→ Vehicle wash & road services</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-red-400">{m.vehicleWash ? `$${m.vehicleWash.toLocaleString()}` : '-'}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-semibold text-red-400 bg-slate-800/30">{formatCurrency(pnlData.ytd?.vehicleWash)}</td>
                      </tr>
                      <tr className="bg-orange-900/30 font-semibold">
                        <td className="px-4 py-2 text-orange-400 sticky left-0 bg-orange-900/30">Total for Other Expenses</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className="px-3 py-2 text-right font-mono text-orange-400">{formatCurrency(m.totalOtherExpenses || m.vehicleExpenses)}</td>
                        ))}
                        <td className="px-4 py-2 text-right font-mono font-bold text-orange-400 bg-orange-900/40">{formatCurrency(pnlData.ytd?.totalOtherExpenses || pnlData.ytd?.vehicleExpenses)}</td>
                      </tr>

                      {/* NET OTHER INCOME */}
                      <tr className="bg-slate-700/30 font-semibold">
                        <td className="px-4 py-2 text-slate-300 sticky left-0 bg-slate-700/30">Net Other Income</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className={`px-3 py-2 text-right font-mono ${(m.totalOther || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatCurrency(m.totalOther)}
                          </td>
                        ))}
                        <td className={`px-4 py-2 text-right font-mono font-semibold bg-slate-700/40 ${(pnlData.ytd?.totalOther || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatCurrency(pnlData.ytd?.totalOther)}
                        </td>
                      </tr>

                      {/* NET INCOME */}
                      <tr className="bg-gradient-to-r from-emerald-900/50 to-slate-900 font-bold text-lg">
                        <td className="px-4 py-4 text-emerald-400 sticky left-0 bg-emerald-900/50">NET INCOME</td>
                        {pnlData.monthly.map(m => (
                          <td key={m.month} className={`px-3 py-4 text-right font-mono ${(m.netIncome || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {formatCurrency(m.netIncome)}
                          </td>
                        ))}
                        <td className={`px-4 py-4 text-right font-mono font-bold text-lg bg-emerald-900/60 ${(pnlData.ytd?.netIncome || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {formatCurrency(Math.abs(pnlData.ytd?.netIncome || 0))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Cost Breakdown Pie Chart */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <h3 className="font-chivo font-semibold text-lg text-white mb-4">Cost Breakdown (YTD)</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Materials', value: pnlData.ytd.materialsCost || 0 },
                          { name: 'Direct Labor', value: pnlData.ytd.laborDirect || 0 },
                          { name: 'Subcontractors', value: pnlData.ytd.subcontractorCosts || 0 },
                          { name: 'Operating Exp', value: pnlData.ytd.totalOpex || 0 },
                          { name: 'Equipment', value: pnlData.ytd.equipmentRental || 0 },
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {[0,1,2,3,4].map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                  <h3 className="font-chivo font-semibold text-lg text-white mb-4">Margin Trends</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={pnlData.monthly.filter(m => m.grossMargin !== null)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="month" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" tickFormatter={(v) => `${v}%`} domain={[0, 50]} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                        formatter={(value) => `${value}%`}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="grossMargin" stroke="#3b82f6" name="Gross Margin %" strokeWidth={2} dot={{ fill: '#3b82f6' }} />
                      <Line type="monotone" dataKey="netMargin" stroke="#10b981" name="Net Margin %" strokeWidth={2} dot={{ fill: '#10b981' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-12 text-center">
              <FileSpreadsheet size={48} className="mx-auto text-slate-600 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No P&L Data Available</h3>
              <p className="text-slate-500">Financial data will appear here once transactions are recorded</p>
            </div>
          )}
        </TabsContent>

        {/* Time Reports Tab */}
        <TabsContent value="time" className="mt-6 space-y-6">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => exportToCSV(employeeHours, "hours_by_employee")}>
              <Download size={16} className="mr-2" />
              Export Employee Hours
            </Button>
            <Button variant="outline" onClick={() => exportToCSV(projectHours, "hours_by_project")}>
              <Download size={16} className="mr-2" />
              Export Project Hours
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hours by Employee */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="font-chivo font-semibold text-lg text-white mb-4">Hours by Employee</h3>
              {employeeHours.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={employeeHours} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="user_name" type="category" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="regular_hours" stackId="a" fill="#3B82F6" name="Regular" />
                    <Bar dataKey="overtime_hours" stackId="a" fill="#F97316" name="Overtime" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  No employee time data
                </div>
              )}
            </div>

            {/* Hours by Project Bar */}
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <h3 className="font-chivo font-semibold text-lg text-white mb-4">Hours by Project</h3>
              {projectHours.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={projectHours.slice(0, 8)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="project_name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="total_hours" fill="#10B981" name="Hours" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">
                  No project time data
                </div>
              )}
            </div>
          </div>

          {/* Employee Hours Table */}
          {employeeHours.length > 0 && (
            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <div className="p-4 border-b bg-slate-800">
                <h3 className="font-semibold text-white">Employee Hours Detail</h3>
              </div>
              <table className="w-full">
                <thead className="bg-slate-800 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Employee</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Regular Hours</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Overtime</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Total Hours</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Entries</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {employeeHours.map((emp) => (
                    <tr key={emp.user_id}>
                      <td className="px-4 py-3 font-medium text-white">{emp.user_name}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{emp.regular_hours?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-amber-600 font-medium">{emp.overtime_hours?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-white">{emp.total_hours?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-slate-400">{emp.entries_count}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-800 border-t">
                  <tr>
                    <td className="px-4 py-3 font-semibold">Total</td>
                    <td className="px-4 py-3 text-right font-semibold">{employeeHours.reduce((s, e) => s + (e.regular_hours || 0), 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-amber-600">{totalOvertime.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-bold">{totalHours.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">{employeeHours.reduce((s, e) => s + (e.entries_count || 0), 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects" className="mt-6 space-y-6">
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => exportToCSV(projectReport, "project_summary")}>
              <Download size={16} className="mr-2" />
              Export Project Summary
            </Button>
          </div>

          {/* Project Summary Table */}
          {projectReport.length > 0 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Project</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Status</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Budget</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Labor</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Materials</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Total Cost</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Hours</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Paid</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {projectReport.map((project) => (
                      <tr key={project.project_id}>
                        <td className="px-4 py-3 font-medium text-white">{project.name}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                            project.status === "active" ? "bg-emerald-100 text-emerald-700" :
                            project.status === "completed" ? "bg-blue-100 text-blue-700" :
                            "bg-slate-800 text-slate-600"
                          }`}>
                            {project.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-slate-600">${project.budget?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-slate-600">${project.labor_cost?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-slate-600">${project.materials_cost?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right font-medium text-amber-600">${project.total_cost?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{project.total_hours?.toFixed(1)}</td>
                        <td className="px-4 py-3 text-right text-emerald-600">${project.paid_amount?.toLocaleString()}</td>
                        <td className={`px-4 py-3 text-right font-semibold ${project.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          ${project.gross_profit?.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
              <FolderKanban size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-400">No project data available</p>
            </div>
          )}
        </TabsContent>

        {/* Payroll Tab */}
        <TabsContent value="payroll" className="mt-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="font-chivo font-semibold text-lg text-white">Approved Timesheets for Payroll</h3>
            <Button onClick={exportTimesheetsToCSV} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
              <Download size={16} className="mr-2" />
              Export for Payroll
            </Button>
          </div>

          {/* Overtime Alert */}
          {totalOvertime > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="text-amber-600 mt-0.5" size={20} />
              <div>
                <h4 className="font-semibold text-amber-800">Overtime Alert</h4>
                <p className="text-sm text-amber-700">
                  {totalOvertime.toFixed(1)} hours of overtime recorded this period. Review before processing payroll.
                </p>
              </div>
            </div>
          )}

          {/* Approved Timesheets */}
          {timesheets.length > 0 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-800 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Employee</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Week</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Regular</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Overtime</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Total</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Approved</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {timesheets.map((ts) => (
                    <tr key={ts.timesheet_id}>
                      <td className="px-4 py-3 font-medium text-white">{ts.user_name}</td>
                      <td className="px-4 py-3 text-slate-600">{ts.week_start} - {ts.week_end}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{ts.regular_hours?.toFixed(2)}</td>
                      <td className={`px-4 py-3 text-right ${ts.overtime_hours > 0 ? "text-amber-600 font-medium" : "text-slate-600"}`}>
                        {ts.overtime_hours?.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-white">{ts.total_hours?.toFixed(2)}</td>
                      <td className="px-4 py-3 text-slate-400 text-sm">
                        {ts.approved_at ? new Date(ts.approved_at).toLocaleDateString() : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
              <Clock size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-400">No approved timesheets ready for payroll</p>
            </div>
          )}
        </TabsContent>

        {/* Time Off Balances Tab */}
        <TabsContent value="timeoff" className="mt-6 space-y-6">
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => exportToCSV(timeOffBalances, "time_off_balances")}>
              <Download size={16} className="mr-2" />
              Export Balances
            </Button>
          </div>

          {timeOffBalances.length > 0 ? (
            <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-800 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Employee</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Policy</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Max Balance</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Used</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-400 uppercase">Available</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {timeOffBalances.map((bal, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-3 font-medium text-white">{bal.user_name}</td>
                      <td className="px-4 py-3 text-slate-600">{bal.policy_name}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{bal.max_balance}h</td>
                      <td className="px-4 py-3 text-right text-slate-600">{bal.used_hours}h</td>
                      <td className={`px-4 py-3 text-right font-semibold ${bal.available_hours > 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {bal.available_hours}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
              <PalmtreeIcon size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-400">No time off balance data</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* P&L Entry Modal */}
      <Dialog open={showPnlEntryModal} onOpenChange={setShowPnlEntryModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingEntry ? 'Edit P&L Entry' : 'Add P&L Entry'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Year</Label>
                <Select 
                  value={pnlEntryForm.year.toString()} 
                  onValueChange={(v) => setPnlEntryForm(prev => ({ ...prev, year: parseInt(v) }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {[2024, 2025, 2026].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Month</Label>
                <Select 
                  value={pnlEntryForm.month.toString()} 
                  onValueChange={(v) => setPnlEntryForm(prev => ({ ...prev, month: parseInt(v) }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {MONTHS.map((month, idx) => (
                      <SelectItem key={idx} value={(idx + 1).toString()}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Section</Label>
              <Select 
                value={pnlEntryForm.section} 
                onValueChange={(v) => setPnlEntryForm(prev => ({ 
                  ...prev, 
                  section: v, 
                  category: PNL_CATEGORIES[v]?.[0]?.id || '' 
                }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="income">Income</SelectItem>
                  <SelectItem value="cogs">Cost of Goods Sold</SelectItem>
                  <SelectItem value="expenses">Expenses</SelectItem>
                  <SelectItem value="otherIncome">Other Income</SelectItem>
                  <SelectItem value="otherExpenses">Other Expenses</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Category</Label>
              <Select 
                value={pnlEntryForm.category} 
                onValueChange={(v) => setPnlEntryForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-72 overflow-y-auto">
                  {PNL_CATEGORIES[pnlEntryForm.section]?.map(cat => {
                    const isSubCategory = cat.type === 'sub_category';
                    const isNested = cat.parent;
                    return (
                      <SelectItem 
                        key={cat.id} 
                        value={cat.id}
                        className={isSubCategory ? 'font-semibold text-emerald-400' : isNested ? 'pl-6 text-slate-300' : 'text-white'}
                      >
                        {isNested ? '→ ' : ''}{cat.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={pnlEntryForm.amount}
                onChange={(e) => setPnlEntryForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
                className="bg-slate-800 border-slate-700 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                {pnlEntryForm.section === 'income' || pnlEntryForm.section === 'otherIncome'
                  ? 'Positive values add to income' 
                  : 'Positive values add to expenses'}
              </p>
            </div>

            <div>
              <Label className="text-slate-300">Description (Optional)</Label>
              <Textarea
                value={pnlEntryForm.description}
                onChange={(e) => setPnlEntryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Add notes about this entry..."
                className="bg-slate-800 border-slate-700 text-white"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowPnlEntryModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSavePnlEntry}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-900"
              >
                <Save size={16} className="mr-2" />
                {editingEntry ? 'Update Entry' : 'Add Entry'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual P&L Entries List */}
      {canEditFinancials && pnlEntries.length > 0 && activeTab === 'pnl' && (
        <div className="fixed bottom-4 right-4 z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-w-md">
            <div className="p-3 border-b border-slate-700 flex items-center justify-between">
              <h4 className="font-semibold text-white text-sm">Manual Entries ({pnlEntries.length})</h4>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => handleOpenPnlEntryModal()}
                className="h-7 text-emerald-400 hover:text-emerald-300"
              >
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {pnlEntries.slice(0, 5).map(entry => (
                <div key={entry.entry_id} className="p-2 border-b border-slate-700/50 flex items-center justify-between hover:bg-slate-700/30">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate">
                      {MONTHS[entry.month - 1]} - {PNL_CATEGORIES[entry.section]?.find(c => c.id === entry.category)?.label || entry.category}
                    </p>
                    <p className="text-xs text-emerald-400 font-mono">${entry.amount.toLocaleString()}</p>
                  </div>
                  <div className="flex gap-1 ml-2">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => handleOpenPnlEntryModal(entry)}
                      className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                    >
                      <Edit2 size={12} />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => handleDeletePnlEntry(entry.entry_id)}
                      className="h-6 w-6 p-0 text-slate-400 hover:text-red-400"
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
              ))}
              {pnlEntries.length > 5 && (
                <p className="text-xs text-slate-500 text-center py-2">+{pnlEntries.length - 5} more entries</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
